import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useI18n } from '../i18n';
import HighlightPhrase from '../components/HighlightPhrase';
import HomeLocationBlock from '../components/HomeLocationBlock';
import WaitlistForm from '../components/WaitlistForm';
import SocialLinks from '../components/SocialLinks';
import { VIDAT_IMG } from '../lib/images';

const HERO_IMG = VIDAT_IMG.manos;
const DIFFERENTIAL_IMG = VIDAT_IMG.estanque1;
const ADIPOSITY_IMG = VIDAT_IMG.estanque2;
const DIABETES_IMG = VIDAT_IMG.monasterio1;
const SENIOR_IMG = VIDAT_IMG.monasterio2;

const Home = () => {
  const { t, content } = useI18n();
  const c = content;

  return (
    <main data-testid="home-page">
      <section className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 bg-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="overline mb-6">{c.home.eyebrow}</div>
              <h1 className="display-xl text-cyan-vidat" data-testid="hero-headline">
                {c.home.headline}
              </h1>
              <p className="mt-8 text-lg text-stone-700 font-light leading-relaxed max-w-xl">
                {c.home.intro1}
              </p>
              <p className="mt-5 text-lg text-stone-700 font-light leading-relaxed max-w-xl">
                {c.home.intro2}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/valoracion-cardiometabolica" className="btn-primary" data-testid="hero-cta-cardio">
                  {t('nav.cardio')} <ArrowUpRight size={16} strokeWidth={1.5} />
                </Link>
                <Link to="/senior-saludable" className="btn-secondary" data-testid="hero-cta-senior">
                  {t('nav.senior')} <ArrowUpRight size={16} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 fade-up fade-up-delay-1">
              <div className="aspect-[4/5] overflow-hidden bg-stone-100">
                <img src={HERO_IMG} alt="" className="w-full h-full object-cover img-grayscale" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24 lg:py-32" data-testid="home-differential">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="overline mb-6">{c.home.differential.eyebrow}</div>
          <h2 className="display-lg text-cyan-vidat max-w-3xl">{c.home.differential.title}</h2>
          <span className="section-rule mt-10 mb-12"></span>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7 prose-vidat">
              <p>{c.home.differential.p1}</p>
              <p>{c.home.differential.p2}</p>
              <p className="!text-2xl !leading-snug !text-accent-vidat font-light pt-4 border-l-2 border-[#1E3A8A] pl-6 my-8">
                {c.home.differential.highlight}
              </p>
              <p>{c.home.differential.p3}</p>
              <p className="!font-medium !text-accent-vidat">{c.home.differential.p4}</p>
              <p>{c.home.differential.p5}</p>
              <p>{c.home.differential.p6}</p>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={DIFFERENTIAL_IMG} alt="" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-32" data-testid="home-cardio-block">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="overline mb-6">{c.home.cardioBlock.eyebrow}</div>
          <h2 className="display-lg text-cyan-vidat max-w-3xl">{c.home.cardioBlock.title}</h2>
          <span className="section-rule mt-10 mb-12"></span>
          <p className="text-lg text-stone-700 font-light max-w-3xl mb-6">{c.home.cardioBlock.lead}</p>
          <p className="text-base text-stone-600 font-light max-w-3xl mb-12 leading-relaxed">{c.home.cardioBlock.audience}</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[1, 2, 3].map((n) => (
              <div key={n} className="border-t border-[#0936F6]/30 pt-6">
                <div className="text-cyan-vidat text-sm font-medium tracking-wide mb-2">
                  {c.home.cardioBlock[`include${n}Title`]}
                </div>
                <div className="text-stone-700 font-light leading-relaxed text-sm">
                  {c.home.cardioBlock[`include${n}Body`]}
                </div>
              </div>
            ))}
          </div>

          <div className="prose-vidat max-w-3xl">
            <p>{c.home.cardioBlock.processing}</p>
            <p>{c.home.cardioBlock.processing2}</p>
          </div>
        </div>
      </section>

      <HighlightPhrase eyebrow={c.home.cardioBlock.modelEyebrow} dark testId="home-cardio-highlight">
        {c.home.cardioBlock.modelHighlight}
      </HighlightPhrase>

      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <h3 className="display-md text-cyan-vidat">{c.home.cardioBlock.whatYouGetTitle}</h3>
              <span className="section-rule mt-8"></span>
            </div>
            <ul className="lg:col-span-7 space-y-5">
              {[1, 2, 3, 4, 5].map((n) => (
                <li key={n} className="flex gap-5 border-b border-stone-200 pb-5">
                  <span className="text-cyan-vidat font-mono text-xs pt-1">0{n}</span>
                  <span className="text-stone-700 font-light leading-relaxed">{c.home.cardioBlock[`get${n}`]}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/programar-visita" className="btn-primary" data-testid="cta-join-first">
              {c.buttons.joinFirstAssessments}
            </Link>
            <Link to="/valoracion-cardiometabolica" className="btn-secondary" data-testid="cta-see-cardio">
              {c.buttons.seeCardio}
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24 lg:py-28" data-testid="home-adiposity-block">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <div className="overline mb-6">{c.adiposity.eyebrow}</div>
              <h2 className="display-lg text-cyan-vidat">{c.adiposity.homeShortTitle}</h2>
              <span className="section-rule mt-10 mb-10"></span>
              <p className="text-lg text-stone-700 font-light leading-relaxed max-w-2xl mb-6">{c.adiposity.homeShortLead}</p>
              <div className="prose-vidat max-w-2xl">
                <p>{c.adiposity.homeShortP1}</p>
                <p>{c.adiposity.homeShortP2}</p>
              </div>
              <div className="mt-10">
                <Link to="/valoracion-adiposidad-metabolismo" className="btn-secondary" data-testid="cta-see-adiposity">
                  {c.buttons.seeAdiposity}
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] overflow-hidden border border-stone-200">
                <img src={ADIPOSITY_IMG} alt="" className="w-full h-full object-cover img-grayscale" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-28" data-testid="home-diabetes-block">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="aspect-[4/5] overflow-hidden border border-stone-200">
                <img src={DIABETES_IMG} alt="" className="w-full h-full object-cover img-grayscale" />
              </div>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="overline mb-6">{c.diabetes.eyebrow}</div>
              <h2 className="display-lg text-cyan-vidat">{c.diabetes.homeShortTitle}</h2>
              <span className="section-rule mt-10 mb-10"></span>
              <p className="text-lg text-stone-700 font-light leading-relaxed max-w-2xl mb-6">{c.diabetes.homeShortLead}</p>
              <div className="prose-vidat max-w-2xl">
                <p>{c.diabetes.homeShortP1}</p>
                <p>{c.diabetes.homeShortP2}</p>
              </div>
              <div className="mt-10">
                <Link to="/valoracion-diabetes-tipo-2" className="btn-secondary" data-testid="cta-see-diabetes">
                  {c.buttons.seeDiabetes}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24 lg:py-28">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <div className="overline mb-8">{c.home.seniorBridge.eyebrow}</div>
          <div className="prose-vidat max-w-3xl">
            <p className="!text-xl">{c.home.seniorBridge.p1}</p>
            <p className="!text-xl">{c.home.seniorBridge.p2}</p>
            <p className="!text-xl !text-accent-vidat">{c.home.seniorBridge.p3}</p>
          </div>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="aspect-[3/4] overflow-hidden">
                <img src={SENIOR_IMG} alt="" className="w-full h-full object-cover img-grayscale" />
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="overline mb-6">{c.home.seniorBlock.eyebrow}</div>
              <h2 className="display-lg text-cyan-vidat">{c.home.seniorBlock.title}</h2>
              <span className="section-rule mt-8 mb-10"></span>
              <div className="prose-vidat">
                <p>{c.home.seniorBlock.p1}</p>
                <p>{c.home.seniorBlock.p2}</p>
                <p>{c.home.seniorBlock.p3}</p>
                <p>{c.home.seniorBlock.p4}</p>
              </div>
              <div className="mt-10">
                <Link to="/senior-saludable" className="btn-secondary" data-testid="cta-expand-senior">
                  {c.buttons.expandInfo}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="overline mb-6">{c.home.rehabBlock.eyebrow}</div>
          <div className="prose-vidat max-w-3xl">
            <p>{c.home.rehabBlock.p1}</p>
            <p>{c.home.rehabBlock.p2}</p>
            <p>{c.home.rehabBlock.p3}</p>
            <p>{c.home.rehabBlock.p4}</p>
            <p className="!text-accent-vidat">{c.home.rehabBlock.p5}</p>
          </div>
          <div className="mt-10">
            <Link to="/rehabilitacion-funcional" className="btn-secondary" data-testid="cta-expand-rehab">
              {c.buttons.expandRehab}
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[#1E3A8A] text-white py-24 lg:py-32" data-testid="two-moments">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-display text-[clamp(1.6rem,3vw,2.4rem)] font-light tracking-tight mb-12">
            {c.home.twoMoments.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="border-t border-white/30 pt-8">
              <div className="font-mono text-xs tracking-widest text-white/60 mb-3">01</div>
              <p className="text-lg font-light leading-relaxed">{c.home.twoMoments.moment1}</p>
            </div>
            <div className="border-t border-white/30 pt-8">
              <div className="font-mono text-xs tracking-widest text-white/60 mb-3">02</div>
              <p className="text-lg font-light leading-relaxed">{c.home.twoMoments.moment2}</p>
            </div>
          </div>
        </div>
      </section>

      <HomeLocationBlock />

      <section className="bg-white py-24 lg:py-32" data-testid="home-waitlist-section">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
          <div className="overline mb-6">VIDAT</div>
          <h2 className="display-md text-cyan-vidat max-w-2xl">{c.location.notify}</h2>
          <span className="section-rule mt-8 mb-14"></span>
          <WaitlistForm testIdPrefix="home-waitlist" />
          <div className="mt-16 pt-10 border-t border-stone-200 flex flex-wrap items-center gap-6">
            <span className="text-[11px] uppercase tracking-[0.22em] text-stone-500">
              {t('footer.follow')}
            </span>
            <SocialLinks variant="light" testIdPrefix="home-waitlist-social" />
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
